import produce from "immer";
import { createAction, createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { CalendarEvent, APICallStatus, SendAnnouncementOptions } from "../../../Types/UnitFeatureTypes";


interface EventRequestState {
  request: {
    loading: boolean;
    success: boolean;
    error: string | null;
    eventId: number;
    createEventAPIStatus: APICallStatus;
    editEventAPIStatus: APICallStatus;
    deleteEventAPIStatus: APICallStatus;
    rsvpEventAPIStatus: APICallStatus;
  };
}

export enum Types {
  CREATE_EVENT_REQUEST = 'CREATE_EVENT_REQUEST',
  CREATE_EVENT_SUCCESS = 'CREATE_EVENT_SUCCESS',
  CREATE_EVENT_FAILURE = 'CREATE_EVENT_FAILURE',
  EDIT_EVENT_REQUEST = 'EDIT_EVENT_REQUEST',
  EDIT_EVENT_SUCCESS = 'EDIT_EVENT_SUCCESS',
  EDIT_EVENT_FAILURE = 'EDIT_EVENT_FAILURE',
  DELETE_EVENT_REQUEST = 'DELETE_EVENT_REQUEST',
  DELETE_EVENT_SUCCESS = 'DELETE_EVENT_SUCCESS',
  DELETE_EVENT_FAILURE = 'DELETE_EVENT_FAILURE',
  RSVP_EVENT_REQUEST = 'RSVP_EVENT_REQUEST',
  RSVP_EVENT_SUCCESS = 'RSVP_EVENT_SUCCESS',
  RSVP_EVENT_FAILURE = 'RSVP_EVENT_FAILURE',
  RESET_STATUS_REQUEST = 'RESET_STATUS_REQUEST',
}

const createEventRequestAction = createAsyncAction(
  Types.CREATE_EVENT_REQUEST,
  Types.CREATE_EVENT_SUCCESS,
  Types.CREATE_EVENT_FAILURE,
)
<
  { eventData: CalendarEvent, },
  { id: number },
  { message: string }
>();

const deleteEventRequestAction = createAsyncAction(
  Types.DELETE_EVENT_REQUEST,
  Types.DELETE_EVENT_SUCCESS,
  Types.DELETE_EVENT_FAILURE,
)
<
  { id: number },
  void,
  { message: string }
>();

const rsvpEventRequestAction = createAsyncAction(
  Types.RSVP_EVENT_REQUEST,
  Types.RSVP_EVENT_SUCCESS,
  Types.RSVP_EVENT_FAILURE,
)
<
  { event_id: string, user_id: number[], unit_id: number },
  void,
  { message: string }
>();

const editEventRequestAction = createAsyncAction(
  Types.EDIT_EVENT_REQUEST,
  Types.EDIT_EVENT_SUCCESS,
  Types.EDIT_EVENT_FAILURE,
)
<
  { eventData: CalendarEvent, },
  { id: string },
  { message: string }
>();

const resetStatusRequestAction = createAction(Types.RESET_STATUS_REQUEST)();

export const Actions = { createEventRequestAction, editEventRequestAction, deleteEventRequestAction, rsvpEventRequestAction, resetStatusRequestAction };

export const initialState: EventRequestState = {
  request: {
    loading: false,
    error: null as string | null,
    createEventAPIStatus: APICallStatus.PENDING,
    editEventAPIStatus: APICallStatus.PENDING,
    deleteEventAPIStatus: APICallStatus.PENDING,
    rsvpEventAPIStatus: APICallStatus.PENDING,
    success: false,
    eventId: -1,
  }
};

export type EventRequestInfoState = typeof initialState;

const createEventRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.createEventAPIStatus = APICallStatus.PENDING;

    return draft;
  });

const createEventRequestSuccess = (state = initialState, action: ActionType<typeof Actions.createEventRequestAction.success>) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = true;
    draft.request.eventId = action.payload.id;
    draft.request.createEventAPIStatus = APICallStatus.SUCCESS; 
  });

const createEventRequestFailure = (state = initialState, action: ActionType<typeof Actions.createEventRequestAction.failure>) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = action.payload.message;
    draft.request.createEventAPIStatus = APICallStatus.FAILURE;
  });

const editEventRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.editEventAPIStatus = APICallStatus.PENDING;
  });

const editEventRequestSuccess = (state = initialState, action: ActionType<typeof Actions.editEventRequestAction.success>) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = true;
    draft.request.eventId = parseInt(action.payload.id);
    draft.request.editEventAPIStatus = APICallStatus.SUCCESS;
  });

const editEventRequestFailure = (state = initialState, action: ActionType<typeof Actions.editEventRequestAction.failure>) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = action.payload.message;
    draft.request.editEventAPIStatus = APICallStatus.FAILURE;
  });

const deleteEventRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.deleteEventAPIStatus = APICallStatus.PENDING;
    draft.request.error = null;
  });

const deleteEventRequestSuccess = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = true;
    draft.request.deleteEventAPIStatus = APICallStatus.SUCCESS;
  });

const deleteEventRequestFailure = (state = initialState, action: ActionType<typeof Actions.deleteEventRequestAction.failure>) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = action.payload.message;
    draft.request.deleteEventAPIStatus = APICallStatus.FAILURE
  });

const rsvpEventRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.rsvpEventAPIStatus = APICallStatus.PENDING;
  });

const rsvpEventRequestSuccess = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = true;
    draft.request.rsvpEventAPIStatus = APICallStatus.SUCCESS;
  });

const rsvpEventRequestFailure = (state = initialState, action: ActionType<typeof Actions.rsvpEventRequestAction.failure>) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = action.payload.message;
    draft.request.rsvpEventAPIStatus = APICallStatus.FAILURE;
  });

const resetStatusRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.error = null;
    draft.request.eventId = -1;
    draft.request.createEventAPIStatus = APICallStatus.PENDING;
    draft.request.editEventAPIStatus = APICallStatus.PENDING;
    draft.request.deleteEventAPIStatus = APICallStatus.PENDING;
    draft.request.rsvpEventAPIStatus = APICallStatus.PENDING;
    return draft;
  });

export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.createEventRequestAction.request, createEventRequestRequest)
  .handleAction(Actions.createEventRequestAction.success, createEventRequestSuccess)
  .handleAction(Actions.createEventRequestAction.failure, createEventRequestFailure)
  .handleAction(Actions.editEventRequestAction.request, editEventRequestRequest)
  .handleAction(Actions.editEventRequestAction.success, editEventRequestSuccess)
  .handleAction(Actions.editEventRequestAction.failure, editEventRequestFailure)
  .handleAction(Actions.deleteEventRequestAction.request, deleteEventRequestRequest)
  .handleAction(Actions.deleteEventRequestAction.success, deleteEventRequestSuccess)
  .handleAction(Actions.deleteEventRequestAction.failure, deleteEventRequestFailure)
  .handleAction(Actions.rsvpEventRequestAction.request, rsvpEventRequestRequest)
  .handleAction(Actions.rsvpEventRequestAction.success, rsvpEventRequestSuccess)
  .handleAction(Actions.rsvpEventRequestAction.failure, rsvpEventRequestFailure)
  .handleAction(Actions.resetStatusRequestAction, resetStatusRequest);
  